import { Text } from '@understory-io/leaf/text';
import { css } from '@understory-io/leaf/css';
import { cx } from '~/styled-system/css';
import { FormattedMessage } from 'react-intl';
import type { ComponentProps } from 'react';
import { SeatCounterVisibility as ProtoSeatCounterVisibility } from '~/lib/api/storefronts-core/rpc/gen/storefronts_v1_pb';
import type { AvailabilityStatus } from '~/domain/availability/index.server';

export type { AvailabilityStatus };

export type SeatCounterVisibility = 'always' | 'limitedOnly' | 'never';

/**
 * Maps the gRPC `SeatCounterVisibility` enum to the string union used by UI
 * components. `UNSPECIFIED` falls back to `'limitedOnly'` — the default the
 * product picked when the storefront has no explicit preference.
 */
export function mapSeatCounterVisibility(
  value: ProtoSeatCounterVisibility
): SeatCounterVisibility {
  switch (value) {
    case ProtoSeatCounterVisibility.ALWAYS:
      return 'always';
    case ProtoSeatCounterVisibility.NEVER:
      return 'never';
    case ProtoSeatCounterVisibility.LIMITED_ONLY:
    case ProtoSeatCounterVisibility.UNSPECIFIED:
    default:
      return 'limitedOnly';
  }
}

export type AvailabilityLabelProps = {
  status: AvailabilityStatus;
  availableSeats: number;
  /** Whether the seat count is shown for available slots. Defaults to `'limitedOnly'`. */
  seatCounterVisibility?: SeatCounterVisibility;
  /** Takes precedence over `seatCounterVisibility` — set when the event's seat
   *  count isn't a number the visitor can act on (no-limit capacity
   *  sentinel). */
  hideSeatCount?: boolean;
  /** See `EventAvailability.bookingMaxBelowSeatCount`. Hides the count unless
   *  `seatCounterVisibility` is `'always'`. */
  bookingMaxBelowSeatCount?: boolean;
  /** Override the Text style. Defaults to `'caption'` to match EventCard. */
  textStyle?: ComponentProps<typeof Text>['style'];
  className?: string;
};

/**
 * Whether an event's seat count may be shown or announced at all, before the
 * per-status rules of `seatCounterVisibility`. Shared by the visible label and
 * the time slot's accessible label so the two can't disagree.
 */
export function isSeatCountSuppressed(
  hideSeatCount: boolean,
  bookingMaxBelowSeatCount: boolean,
  seatCounterVisibility: SeatCounterVisibility
): boolean {
  return (
    hideSeatCount ||
    (bookingMaxBelowSeatCount && seatCounterVisibility !== 'always')
  );
}

/**
 * Renders the right "X seats left" / "Sold out" / "Unavailable" label for an
 * availability status, with the appropriate color treatment. The seat count
 * for the `'available'` status is gated by `seatCounterVisibility` so the
 * storefront setting from `storefronts-core` can opt in or out.
 */
export function AvailabilityLabel({
  status,
  availableSeats,
  seatCounterVisibility = 'limitedOnly',
  hideSeatCount = false,
  bookingMaxBelowSeatCount = false,
  textStyle = 'caption',
  className,
}: AvailabilityLabelProps) {
  if (status === 'soldOut') {
    return (
      <Text
        style={textStyle}
        aria-hidden="true"
        color="tertiary"
        className={className}
      >
        <FormattedMessage
          defaultMessage="Sold out"
          description="Availability label when an event is sold out"
        />
      </Text>
    );
  }

  const showSeatCount =
    !isSeatCountSuppressed(
      hideSeatCount,
      bookingMaxBelowSeatCount,
      seatCounterVisibility
    ) &&
    (seatCounterVisibility === 'always' ||
      (seatCounterVisibility === 'limitedOnly' &&
        (status === 'limited' || status === 'unavailable')));

  if (!showSeatCount) return null;

  if (status === 'limited') {
    return (
      <Text
        style={textStyle}
        aria-hidden="true"
        className={cx(negativeColor, className)}
      >
        <FormattedMessage
          defaultMessage="{count, plural, one {# spot} other {# spots}} left"
          description="Availability label when seats are running out"
          values={{ count: availableSeats }}
        />
      </Text>
    );
  }

  if (status === 'unavailable') {
    return (
      <Text
        style={textStyle}
        aria-hidden="true"
        className={cx(negativeColor, className)}
      >
        <FormattedMessage
          defaultMessage="{count} available {count, plural, one {spot} other {spots}}"
          description="Availability label when an event cannot accommodate the current ticket selection"
          values={{ count: availableSeats }}
        />
      </Text>
    );
  }

  return (
    <Text
      style={textStyle}
      aria-hidden="true"
      color="secondary"
      className={className}
    >
      <FormattedMessage
        defaultMessage="{count} available {count, plural, one {spot} other {spots}}"
        description="Availability label when an event has open seats"
        values={{ count: availableSeats }}
      />
    </Text>
  );
}

const negativeColor = css({ color: 'status.negative.bold' });
